/**
 * @typedef {Record<string, any>} TierPriceMap
 * @typedef {{ quantities: Record<string, number>, prices: Record<string, number> }} PriceTier
 * @typedef {{
 *   base_price?: { prices: Record<string, number> },
 *   [key: string]: any
 * }} TierPricing
 *
 * @typedef {{
 *   priceLevel?: string,
 *   isTeamAdmin?: boolean
 * }} TeamStorePriceOptions
 */

class TeamStorePriceCalculator {
  /**
   * @param {Record<string, any>} metaobject
   * @param {{
   *   priceLevel?: string,
   *   isTeamAdmin?: boolean
   * }} teamData
   */
  constructor(metaobject = {}, teamData = {}) {
    this.meta = metaobject;
    this.team = teamData;

    this.tierPricing =
      typeof this.meta.tier_pricing === 'string'
        ? JSON.parse(this.meta.tier_pricing)
        : this.meta.tier_pricing || null;

    this.upcharge = Number(this.meta.upcharge_price || 0);

    this.override = Number(this.meta.price_override || 0);
  }

  /**
   * Returns price as number for the given quantity,
   * @param {number} quantity
   * @returns {number}
   */
  getPrice(quantity = 1) {
    if (this.override) {
      return this._withUpcharge(this.override);
    }

    if (this.team.isTeamAdmin && this.tierPricing) {
      const priceTier = this._getPriceTier();
      if (priceTier) {
        return this._getTierBasedPrice(priceTier, quantity);
      }
    }

    const base = this.tierPricing?.base_price?.prices?.['Price 1'];
    return this._withUpcharge(base);
  }

  /**
   * Finds appropriate price tier configuration
   * @returns {PriceTier|null}
   * @private
   */
  _getPriceTier() {
    const key = this.team.priceLevel?.toLowerCase().replace(/ /g, '_');

    return key ? this.tierPricing?.[key] : null;
  }

  /**
   * Tier threshold pricing logic
   * @param {PriceTier} tier
   * @param {number} quantity
   * @returns {number}
   * @private
   */
  _getTierBasedPrice(tier, quantity) {
    const quantities = Object.values(tier.quantities);
    const prices = Object.values(tier.prices);

    if (!quantities.length || !prices.length) {
      return this._withUpcharge(0);
    }

    const maxQty = Math.max(...quantities);

    const index =
      quantity >= maxQty
        ? quantities.length - 1
        : Math.max(quantities.findIndex((q) => q >= quantity) - 1, 0);

    return this._withUpcharge(prices[index]);
  }

  /**
   * @param {number|string|undefined|null} price
   * @returns {number}
   * @private
   */
  _withUpcharge(price) {
    return Number(price || 0) + this.upcharge;
  }
}

/**
 * Validate Team Store context required to safely submit cart actions.
 * @param {Record<string, any>|null|undefined} [teamProductData]
 * @param {Record<string, any>|null|undefined} [metaobject]
 * @returns {boolean}
 */
function hasRequiredTeamStoreContext(
  teamProductData = window.teamProductData || {},
  metaobject = undefined
) {
  const data = teamProductData || {};
  const opportunityRaw = String(data.opportunityNumber ?? '').trim();
  const normalizedOpportunity = opportunityRaw.toLowerCase();
  const hasOpportunity =
    opportunityRaw &&
    normalizedOpportunity !== 'null' &&
    normalizedOpportunity !== 'undefined';

  const resolvedMetaobject = metaobject ?? data.metaobject ?? null;
  const hasMetaobject =
    Boolean(resolvedMetaobject) &&
    (typeof resolvedMetaobject !== 'object' ||
      Object.keys(resolvedMetaobject).length > 0);

  return Boolean(hasOpportunity && hasMetaobject);
}

window.teamStoreUtilities = {};
window.teamStoreUtilities.PriceCalculator = TeamStorePriceCalculator;
window.teamStoreUtilities.hasRequiredContext = hasRequiredTeamStoreContext;
